import { MS_PER_DAY } from "@/lib/constants";
import { dates } from "@/lib/dates";
import { CandidacyStatus, PingType } from "@/types";

// Minimal ping shape — covers both guac's LastPing and nova's
// CandidacyCommunication.
interface HumanPing {
  at: string;
  type: PingType | string;
  sentByAgent?: boolean;
}

// Statuses at or beyond ENRICHANDCHECK. Deliberately a status set rather than
// a pipeline-stage comparison: rejected/closed statuses have no stage and must
// never count, and stage buckets group statuses the guardrail splits on.
const guardedStatuses = new Set<CandidacyStatus>([
  CandidacyStatus.ENRICHANDCHECK,
  CandidacyStatus.READYTOSEND,
  CandidacyStatus.BATCHSENT,
  CandidacyStatus.MATCHED,
  CandidacyStatus.INTERVIEW,
  CandidacyStatus.OFFER,
  CandidacyStatus.PLACED,
  CandidacyStatus.EMPLOYED,
]);

const pingTypeLabel: Partial<Record<string, string>> = {
  [PingType.CALL]: "phone",
  [PingType.EMAIL]: "email",
  [PingType.MESSAGE]: "text",
};

const isGuardedStatus = (status: CandidacyStatus | null | undefined) =>
  !!status && guardedStatuses.has(status);

// Guardrails before handing a candidate to the AI scheduling agent — the agent
// should not text someone a human is actively working or who is already deep
// in another pipeline.
export const agentGuardrails = {
  /**
   * Reasons the AI scheduling agent should not reach out to this candidate.
   * Empty array means no guardrail applies.
   */
  getBlockReasons: ({
    candidacyStatus,
    activePipelines,
    lastHumanCommunication,
    lastCommunication,
  }: {
    candidacyStatus?: CandidacyStatus | null;
    activePipelines?: { company: string; status: CandidacyStatus }[] | null;
    lastHumanCommunication?: HumanPing | null;
    lastCommunication?: HumanPing | null;
  }): string[] => {
    const reasons: string[] = [];

    if (isGuardedStatus(candidacyStatus)) {
      reasons.push("They are already at or past Prep to Send on this job.");
    }
    const guardedPipeline = activePipelines?.find((pipeline) =>
      isGuardedStatus(pipeline.status),
    );
    if (guardedPipeline) {
      reasons.push(
        `They are at or past Prep to Send in an active pipeline at ${guardedPipeline.company}.`,
      );
    }

    // Prefer the explicit human ping; fall back to the last communication when
    // it is known to be human-sent.
    const humanPing =
      lastHumanCommunication ??
      (lastCommunication?.sentByAgent === false ? lastCommunication : null);
    if (humanPing && Date.now() - new Date(humanPing.at).getTime() < MS_PER_DAY) {
      const channel = pingTypeLabel[humanPing.type] ?? "message";
      reasons.push(
        `A human reached out by ${channel} ${dates.timeAgoForPipeline(humanPing.at)}.`,
      );
    }

    return reasons;
  },
};